import React, { useEffect, useRef, useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { parseDateFromISO8601, stringifyDateToISO8601 } from '../../../util/dates';

// Import shared components
import {
  FieldDateRangeController,
  Form,
  LocationAutocompleteInput,
  Modal,
} from '../../../components';

import css from './SearchPill.module.css';

const identity = v => v;

/**
 * Turn the URL's search params into the pill's form values.
 *
 * @param {Object} params parsed search params (address, bounds, origin, dates)
 * @returns {Object} { location, dates }
 */
export const searchPillInitialValues = params => {
  const { address, bounds, origin, dates } = params || {};
  const [start, end] = typeof dates === 'string' ? dates.split(',') : [];
  const startDate = start ? parseDateFromISO8601(start) : null;
  const endDate = end ? parseDateFromISO8601(end) : null;

  return {
    location:
      address && bounds ? { search: address, selectedPlace: { address, bounds, origin } } : null,
    dates: startDate && endDate ? { startDate, endDate } : null,
  };
};

/**
 * Turn the pill's form values into search params for the search page.
 * A cleared field removes its params, so that the search is not stuck with an old value.
 *
 * @param {Object} values { location, dates }
 * @param {boolean} useOrigin whether the marketplace sorts by distance from the searched place
 * @returns {Object} search params
 */
export const searchPillToSearchParams = (values, useOrigin) => {
  const { location, dates } = values || {};
  const { selectedPlace } = location || {};
  const { startDate, endDate } = dates || {};

  const locationParams = selectedPlace
    ? {
        address: location.search || selectedPlace.address,
        bounds: selectedPlace.bounds,
        ...(useOrigin ? { origin: selectedPlace.origin } : {}),
      }
    : {};
  const datesParam =
    startDate && endDate
      ? `${stringifyDateToISO8601(startDate)},${stringifyDateToISO8601(endDate)}`
      : undefined;

  return { ...locationParams, dates: datesParam };
};

// "Oct 10 – 17", or "Oct 30 – Nov 2" across months
const formatDates = (dates, intl) => {
  const { startDate, endDate } = dates || {};
  if (!startDate || !endDate) {
    return null;
  }
  const sameMonth = startDate.getMonth() === endDate.getMonth();
  const start = intl.formatDate(startDate, { month: 'short', day: 'numeric' });
  const end = sameMonth
    ? intl.formatDate(endDate, { day: 'numeric' })
    : intl.formatDate(endDate, { month: 'short', day: 'numeric' });
  return `${start} – ${end}`;
};

const nightCount = dates => {
  const { startDate, endDate } = dates || {};
  return startDate && endDate ? Math.round((endDate - startDate) / (24 * 60 * 60 * 1000)) : 0;
};

const LocationField = props => {
  const { id, intl, className, inputClassName } = props;
  // "Current location" gives a place without an address: the input then says so
  const [isCurrentLocation, setIsCurrentLocation] = useState(false);
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;
        const handleChange = value => {
          onChange(value);
          setIsCurrentLocation(!!value?.selectedPlace && value.selectedPlace.address === '');
        };
        return (
          <LocationAutocompleteInput
            id={id}
            className={className}
            inputClassName={inputClassName}
            iconClassName={css.hiddenIcon}
            predictionsClassName={css.predictions}
            useDarkText
            isCurrentLocation={isCurrentLocation}
            placeholder={intl.formatMessage({
              id: isCurrentLocation ? 'SearchPill.currentLocation' : 'SearchPill.wherePlaceholder',
            })}
            closeOnBlur
            input={{ ...restInput, onChange: handleChange }}
            meta={meta}
            ariaLabel={intl.formatMessage({ id: 'SearchPill.where' })}
          />
        );
      }}
    />
  );
};

/**
 * The fields of the pill: "Where" and "When", then the search button.
 * On a desktop they sit in one row; in the mobile modal they are stacked.
 */
const SearchPillForm = props => {
  const { initialValues, onSubmit, idPrefix, stacked = false, className } = props;
  const intl = useIntl();
  const [datesOpen, setDatesOpen] = useState(false);
  const datesRef = useRef(null);

  // Close the calendar when clicking outside of it
  useEffect(() => {
    if (!datesOpen || stacked) {
      return;
    }
    const onDocumentClick = e => {
      if (datesRef.current && !datesRef.current.contains(e.target)) {
        setDatesOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [datesOpen, stacked]);

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={values => {
        setDatesOpen(false);
        onSubmit(values);
      }}
      render={({ handleSubmit, values, form }) => {
        const datesLabel = formatDates(values.dates, intl);

        const calendar = (
          <div className={css.calendar}>
            <FieldDateRangeController name="dates" minimumNights={1} />
            <div className={css.calendarFooter}>
              <button
                type="button"
                className={css.textButton}
                onClick={() => form.change('dates', null)}
              >
                <FormattedMessage id="SearchPill.clearDates" />
              </button>
              {stacked ? null : (
                <button
                  type="button"
                  className={css.doneButton}
                  onClick={() => setDatesOpen(false)}
                >
                  <FormattedMessage id="SearchPill.done" />
                </button>
              )}
            </div>
          </div>
        );

        return (
          <Form
            onSubmit={handleSubmit}
            className={classNames(stacked ? css.stacked : css.pill, className)}
            enforcePagePreloadFor="SearchPage"
          >
            <div className={css.segment}>
              <span className={css.segmentLabel}>
                <FormattedMessage id="SearchPill.where" />
              </span>
              <LocationField
                id={`${idPrefix}-location`}
                intl={intl}
                className={css.locationRoot}
                inputClassName={css.locationInput}
              />
            </div>

            {stacked ? null : <span className={css.divider} aria-hidden="true" />}

            <div className={css.segment} ref={datesRef}>
              <span className={css.segmentLabel}>
                <FormattedMessage id="SearchPill.when" />
              </span>
              {stacked ? (
                calendar
              ) : (
                <>
                  <button
                    type="button"
                    className={classNames(css.datesButton, { [css.placeholder]: !datesLabel })}
                    onClick={() => setDatesOpen(!datesOpen)}
                    aria-expanded={datesOpen}
                  >
                    {datesLabel || intl.formatMessage({ id: 'SearchPill.addDates' })}
                  </button>
                  {datesOpen ? <div className={css.datesPopup}>{calendar}</div> : null}
                </>
              )}
            </div>

            <button type="submit" className={css.searchButton}>
              <FormattedMessage id="SearchPill.search" />
            </button>
          </Form>
        );
      }}
    />
  );
};

/**
 * Search pill from the design handoff (03_Search_Desktop / 02_Search_Mobile): "Where" | "When" |
 * Search. On the desktop it's a row of fields in the top bar. On mobile it's a button showing
 * the current place and dates, which opens the same fields in a modal.
 *
 * @component
 * @param {Object} props
 * @param {'desktop'|'mobile'} props.variant
 * @param {Object} props.initialValues { location, dates } (see searchPillInitialValues)
 * @param {Function} props.onSubmit called with { location, dates }
 * @param {Function?} props.onManageDisableScrolling needed by the mobile modal
 * @param {string?} props.className
 * @returns {JSX.Element}
 */
const SearchPill = props => {
  const {
    variant = 'desktop',
    initialValues,
    onSubmit,
    onManageDisableScrolling,
    className,
  } = props;
  const intl = useIntl();
  const [isModalOpen, setModalOpen] = useState(false);

  if (variant === 'desktop') {
    return (
      <SearchPillForm
        idPrefix="search-pill-desktop"
        initialValues={initialValues}
        onSubmit={onSubmit}
        className={className}
      />
    );
  }

  const place = initialValues?.location?.search;
  const datesLabel = formatDates(initialValues?.dates, intl);
  const nights = nightCount(initialValues?.dates);
  const subtitle = datesLabel
    ? intl.formatMessage({ id: 'SearchPill.datesWithNights' }, { dates: datesLabel, nights })
    : intl.formatMessage({ id: 'SearchPill.anyDates' });

  return (
    <>
      <button
        type="button"
        className={classNames(css.mobileButton, className)}
        onClick={() => setModalOpen(true)}
      >
        <span className={css.mobileIcon} aria-hidden="true" />
        <span className={css.mobileText}>
          <span className={css.mobilePlace}>
            {place || intl.formatMessage({ id: 'SearchPill.wherePlaceholder' })}
          </span>
          <span className={css.mobileDates}>{subtitle}</span>
        </span>
      </button>
      <Modal
        id="SearchPillMobile"
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
      >
        <SearchPillForm
          idPrefix="search-pill-mobile"
          stacked
          initialValues={initialValues}
          onSubmit={values => {
            setModalOpen(false);
            onSubmit(values);
          }}
        />
      </Modal>
    </>
  );
};

export default SearchPill;
